/*
  BluFood Kollektionsseite (bf D2–D7) – <bf-collection> aus sections/bf-main-collection.liquid
  - Filter, Sortierung, Chips und Seiten laden ohne Neuladen (Section Rendering API)
  - URL wird mitgefuehrt (teilbar, Zurueck-Taste funktioniert)
  - Filter mobil als Schublade (Fokus bleibt in der Schublade, Escape schliesst)
  - "Weiterlesen" fuer lange Kollektionstexte (sections/bf-collection-seo.liquid)
  Ohne JavaScript funktionieren alle Links und das Filter-Formular als normale Seitenaufrufe.
*/

const LOCK = 'bf-coll-lock'

class BfCollection extends HTMLElement {
  connectedCallback () {
    if (this._ready) return
    this._ready = true
    this.sectionId = this.dataset.sectionId
    this.addEventListener('change', (e) => this.onChange(e))
    this.addEventListener('submit', (e) => {
      if (!e.target.matches('[data-bf-coll-form]')) return
      e.preventDefault()
      this.apply()
    })
    this.addEventListener('click', (e) => this.onClick(e))
    this.addEventListener('keydown', (e) => this.onKey(e))
    this.onPop = () => this.load(window.location.href, false)
    window.addEventListener('popstate', this.onPop)
    this.mq = window.matchMedia('(max-width: 989px)')
    this.onMq = () => { if (!this.mq.matches) this.toggleDrawer(false) }
    if (this.mq.addEventListener) this.mq.addEventListener('change', this.onMq)
  }

  disconnectedCallback () {
    window.removeEventListener('popstate', this.onPop)
    if (this.mq && this.mq.removeEventListener) this.mq.removeEventListener('change', this.onMq)
    document.documentElement.classList.remove(LOCK)
  }

  get form () { return this.querySelector('[data-bf-coll-form]') }

  onChange (e) {
    const t = e.target
    if (!t.form || !t.form.matches('[data-bf-coll-form]')) return
    clearTimeout(this.timer)
    // Preisfelder melden "change" erst beim Verlassen oder Enter
    this.timer = setTimeout(() => this.apply(), t.type === 'checkbox' ? 150 : 0)
  }

  onClick (e) {
    const link = e.target.closest('a[data-bf-coll-link]')
    if (link && this.contains(link)) {
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return
      const url = new URL(link.href, window.location.href)
      if (url.origin !== window.location.origin || url.pathname !== window.location.pathname) return
      e.preventDefault()
      this.load(url.href, true, link.hasAttribute('data-bf-coll-scroll'))
      return
    }
    if (e.target.closest('[data-bf-coll-open]')) {
      this.opener = e.target.closest('[data-bf-coll-open]')
      this.toggleDrawer(true)
      return
    }
    if (e.target.closest('[data-bf-coll-close]')) this.toggleDrawer(false)
  }

  onKey (e) {
    if (!this.classList.contains('is-filter-open')) return
    if (e.key === 'Escape') {
      e.preventDefault()
      this.toggleDrawer(false)
      return
    }
    if (e.key !== 'Tab') return
    const aside = this.querySelector('[data-bf-coll-aside]')
    if (!aside) return
    const items = [...aside.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select')]
      .filter((el) => el.offsetParent !== null)
    if (!items.length) return
    const first = items[0]
    const last = items[items.length - 1]
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault()
      last.focus()
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault()
      first.focus()
    }
  }

  toggleDrawer (open) {
    const was = this.classList.contains('is-filter-open')
    if (open === was) return
    this.classList.toggle('is-filter-open', open)
    document.documentElement.classList.toggle(LOCK, open && this.mq.matches)
    const btn = this.querySelector('[data-bf-coll-open]')
    if (btn) btn.setAttribute('aria-expanded', String(open))
    const aside = this.querySelector('[data-bf-coll-aside]')
    if (aside) aside.setAttribute('aria-modal', String(open && this.mq.matches))
    if (open) {
      const close = this.querySelector('.bf-coll__drawer-close')
      if (close) close.focus({ preventScroll: true })
    } else {
      const back = this.querySelector('[data-bf-coll-open]')
      if (back && this.mq.matches) back.focus({ preventScroll: true })
    }
  }

  params () {
    const out = new URLSearchParams()
    const form = this.form
    if (!form) return out
    const sort = this.querySelector('select[name="sort_by"]')
    const sortDefault = sort ? sort.dataset.default : ''
    for (const [key, value] of new FormData(form)) {
      if (String(value).trim() === '') continue
      // Standard-Sortierung nicht in die URL schreiben
      if (key === 'sort_by' && value === sortDefault) continue
      out.append(key, value)
    }
    return out
  }

  apply () {
    const p = this.params()
    const query = p.toString()
    this.load(window.location.pathname + (query ? '?' + query : ''), true)
  }

  async load (href, push, scroll) {
    const url = new URL(href, window.location.href)
    const fetchUrl = new URL(url.href)
    fetchUrl.searchParams.set('section_id', this.sectionId)
    if (this.ctrl) this.ctrl.abort()
    this.ctrl = new AbortController()
    this.classList.add('is-loading')
    this.setAttribute('aria-busy', 'true')
    try {
      const res = await fetch(fetchUrl.href, { signal: this.ctrl.signal, credentials: 'same-origin' })
      if (!res.ok) throw new Error('HTTP ' + res.status)
      const html = await res.text()
      const doc = new DOMParser().parseFromString(html, 'text/html')
      const fresh = doc.querySelector('bf-collection')
      if (!fresh) throw new Error('Section fehlt')
      const active = document.activeElement
      const activeId = active && this.contains(active) ? active.id : ''
      this.innerHTML = fresh.innerHTML
      this.classList.toggle('bf-coll--with-filters', fresh.classList.contains('bf-coll--with-filters'))
      if (this.classList.contains('is-filter-open')) {
        const btn = this.querySelector('[data-bf-coll-open]')
        if (btn) btn.setAttribute('aria-expanded', 'true')
      }
      if (push) window.history.pushState({ bfColl: true }, '', url.pathname + url.search)
      if (activeId) {
        const el = this.querySelector('#' + CSS.escape(activeId))
        if (el) el.focus({ preventScroll: true })
      }
      if (scroll) {
        const top = this.querySelector('[data-bf-coll-top]')
        if (top) top.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }
      document.dispatchEvent(new CustomEvent('bf:collection:updated', { detail: { url: url.href } }))
    } catch (err) {
      if (err.name === 'AbortError') return
      window.location.href = url.href
    } finally {
      this.classList.remove('is-loading')
      this.removeAttribute('aria-busy')
    }
  }
}

if (!customElements.get('bf-collection')) customElements.define('bf-collection', BfCollection)

/* ---------- "Weiterlesen" fuer lange Texte ---------- */
function setupClamp () {
  document.querySelectorAll('[data-bf-clamp]:not([data-bf-clamp-ready])').forEach((box) => {
    box.setAttribute('data-bf-clamp-ready', '')
    const btn = document.querySelector(`[data-bf-clamp-toggle][aria-controls="${box.id}"]`)
    if (!btn) return
    const set = (open) => {
      box.classList.toggle('is-clamped', !open)
      btn.setAttribute('aria-expanded', String(open))
      btn.textContent = open ? btn.dataset.less : btn.dataset.more
    }
    set(false)
    btn.hidden = false
    btn.addEventListener('click', () => set(box.classList.contains('is-clamped')))
  })
}
setupClamp()
